import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'

import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useCurrentSession } from '~/hooks/useCurrentSession'
import type {
  ApproverIdentity,
  ApproverIdentityResolver,
  ApproverTarget,
} from '~/pages/SpotBidding/models/approverIdentity'
import { judgeApproverIdentity } from '~/pages/SpotBidding/models/approverIdentity'

/**
 * 案件 1 件について、 現在ユーザーが当該承認段階の承認者か・申請者本人かを、 案件が持つ
 * 承認申請の時点の承認者の写しから識別する (AC-BSP-098)。 承認状態バナー・承認確認モーダル・
 * 差戻しダイアログが共通に使う。
 */
export function useApproverIdentity(): ApproverIdentityResolver {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const {
    data: session,
    isPending: isSessionPending,
    isError: isSessionError,
  } = useCurrentSession()
  const memberId = session?.memberId

  return useCallback(
    (target: ApproverTarget): ApproverIdentity => {
      const { noticeApproverMemberIds, ...judgement } = judgeApproverIdentity(
        target,
        { isPending: isSessionPending, isError: isSessionError, memberId },
      )
      return {
        ...judgement,
        approversLabel:
          noticeApproverMemberIds.length === 0
            ? t('page:biddingSpot.approverWarning.approversNotSet')
            : noticeApproverMemberIds
                .map((id) => resolveDisplayName(id) ?? '—')
                .join(t('page:biddingSpot.approverWarning.approverSeparator')),
      }
    },
    [isSessionError, isSessionPending, memberId, resolveDisplayName, t],
  )
}
